import { useGetGlobalAttributes, useOwnerProps } from '@elliemae/ds-props-helpers';
import { mergeRefs } from '@elliemae/ds-system';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useContext, useMemo } from 'react';
import { ComboBoxContext } from '../../ComboBoxCTX.js';
import { ComboboxDataTestid } from '../../ComboboxDataTestids.js';
import { isArrayOfItems } from '../../utils/listHelper.js';
import {
  StyledHiddenDiv,
  StyledInput,
  StyledInputPlaceHolder,
  StyledInputWidthReference,
  StyledInputWrapper,
  StyleHiddenInput,
} from './styled.js';
import { useControlsInput } from './useControlsInput.js';
import { useKeyboardNavigation } from './useKeyboardNavigation.js';

export const ControlsInput = (): JSX.Element => {
  const {
    props,
    props: {
      autoFocus,
      inline,
      selectedValues,
      placeholder,
      disabled,
      innerRef,
      isLoading,
      onlySelectable,
      applyAriaDisabled,
      readOnly,
      ...restProps
    },
    focusOptionIdx,
    hasFocus,
    internalRef,
    inputValue,
    setInputValue,
    menuState,
    instanceUid,
  } = useContext(ComboBoxContext);
  const { onInputKeyDown } = useKeyboardNavigation();

  const { spanReference, width, spanReferenceText, showPlaceholder, handleOnBlur, handleOnChange, handleOnFocus } =
    useControlsInput();

  const { getOwnerProps, getOwnerPropsArguments } = useOwnerProps(props);

  const {
    tabIndex,
    id,
    'aria-describedby': ariaDescribedBy,
    ...globalAttrs
  } = useGetGlobalAttributes(restProps, {
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
  });

  const actualAriaDescribedBy = useMemo(() => {
    if (id) {
      return `${ariaDescribedBy ?? ''} ${id}_current_placeholder ${id}_feedback_message ${id}_error_message`;
    }
    return ariaDescribedBy;
  }, [ariaDescribedBy, id]);

  const currentPlaceholder = useMemo(() => {
    if (isArrayOfItems(selectedValues)) {
      return `options ${selectedValues.map((item) => item.label).join(', ')} selected. ${readOnly ? 'Read only' : ''}`;
    }
    if (typeof selectedValues?.label === 'string') {
      return `option ${selectedValues.label} selected. ${readOnly ? 'Read only' : ''}`;
    }
    if (readOnly) {
      return `${placeholder}. Read only`;
    }
    return placeholder;
  }, [placeholder, selectedValues, readOnly]);

  const activeDescendant = useMemo(() => {
    if (!isLoading && hasFocus && menuState) {
      return focusOptionIdx;
    }
    return undefined;
  }, [focusOptionIdx, hasFocus, isLoading, menuState]);

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setInputValue(e.clipboardData.getData('text/plain'));
    internalRef.current?.focus();
  };
  return (
    <StyledInputWrapper>
      <StyledInputWidthReference innerRef={spanReference}>{spanReferenceText}</StyledInputWidthReference>
      {showPlaceholder && (
        <StyledInputPlaceHolder aria-hidden="true">
          <SimpleTruncatedTooltipText value={showPlaceholder}></SimpleTruncatedTooltipText>
        </StyledInputPlaceHolder>
      )}

      {/* The purpose of this div is to always have the 'combo-listbox-{uid}' present in the DOM */}
      {/* Fix for PUI-11597 */}
      {menuState === false && !inline ? <StyledHiddenDiv id={`combo-listbox-${instanceUid}`}></StyledHiddenDiv> : null}

      {/* The announcement of currentPlaceholder is being overridden by the value of actualAriaDescribedBy */}
      {/* and since this variable is necessary for the annoucement of FormLayoutBlockItem error and feedback */}
      {/* messages, we concatenate the value of actualPlaceholder with that aria description */}
      {/* Fix for PUI-9609 */}
      <StyledHiddenDiv id={`${id}_current_placeholder`}>{currentPlaceholder}</StyledHiddenDiv>

      <StyledInput
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        readOnly={readOnly || onlySelectable}
        {...globalAttrs}
        id={id}
        data-testid={ComboboxDataTestid.INPUT}
        autoFocus={autoFocus}
        placeholder={currentPlaceholder}
        aria-activedescendant={activeDescendant}
        aria-expanded={menuState}
        aria-controls={`combo-listbox-${instanceUid}`}
        aria-describedby={actualAriaDescribedBy}
        aria-autocomplete="list"
        role="combobox"
        disabled={disabled}
        applyAriaDisabled={applyAriaDisabled}
        style={{ width }}
        innerRef={mergeRefs(innerRef, internalRef)}
        value={inputValue}
        type="text"
        onKeyDown={onInputKeyDown}
        onChange={handleOnChange}
        tabIndex={tabIndex}
        withoutCaret={onlySelectable}
        autoComplete="off"
        aria-readonly={readOnly}
      />
      {/* This input is used to handle the paste event */}
      <StyleHiddenInput
        type="text"
        disabled={disabled || applyAriaDisabled}
        readOnly={readOnly}
        name="hidden"
        tabIndex={-1}
        onPaste={handleOnPaste}
        aria-hidden="true"
        isDisabled={disabled || applyAriaDisabled}
      />
    </StyledInputWrapper>
  );
};
